/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useCallback, useMemo } from "react";
import { useEffect } from "react";
import { PACE_DASHBOARDS } from "../../../utility/constants";
import { checkDashboardNameErrors, checkDashboardURIErrors, deepEqual } from "../../../utility/helpers";
import {
	addDashboardUrlConfigAction,
	clearDashboardConfigChangesAction,
	fetchDashboardUrlConfigList,
	modifyDashboardUrlConfigAction,
	removeDashboardUrlConfigAction,
	reorderDashboardUrlsAction,
	saveDashboardUrlConfigList,
} from "./actions";

import { dashboardConfigInitialState, dashboardUrlConfigReducer } from "./reducer";

const Context = React.createContext();

export const useDashboardUrlConfigs = () => React.useContext(Context);

export const checkDuplicateNames = (reportName, dashboardUrls, startIdx) => {
	for (let urlIdx = startIdx; urlIdx < dashboardUrls.length; urlIdx++) {
		const url = dashboardUrls[urlIdx];
		if (url.reportName === reportName) {
			return "Report name already exists. Please try with a different name.";
		}
	}
	return false;
};

export const checkDuplicateDashboards = (reportUrl, dashboardUrls, startIdx) => {
	for (let urlIdx = startIdx; urlIdx < dashboardUrls.length; urlIdx++) {
		const url = dashboardUrls[urlIdx];
		if (url.reportUrl === reportUrl) {
			return `This dashboard has been already configured with the name '${url.reportName}'.`;
		}
	}
	return false;
};

export const DashboardUrlConfigsProvider = ({ children }) => {
	const [state, dispatch] = React.useReducer(dashboardUrlConfigReducer, dashboardConfigInitialState);
	const { dashboardUrls, dashboardUrlsBackup } = state;
	const isModified = useMemo(
		() => !deepEqual(dashboardUrls, dashboardUrlsBackup) || false,
		[dashboardUrls, dashboardUrlsBackup],
	);

	const validateDashboardUrls = useMemo(() => {
		let isInValidForm = false;
		for (let urlIdx = 0; urlIdx < dashboardUrls.length; urlIdx++) {
			const dashboardUrl = dashboardUrls[urlIdx];
			const reportNameError =
				checkDashboardNameErrors(dashboardUrl.reportName, PACE_DASHBOARDS.REPORT_NAME_MAX_LEN) ||
				checkDuplicateNames(dashboardUrl.reportName, dashboardUrls, urlIdx + 1);
			const reportUrlError =
				checkDashboardURIErrors(dashboardUrl.reportUrl) ||
				checkDuplicateDashboards(dashboardUrl.reportUrl, dashboardUrls, urlIdx + 1);
			dashboardUrl.errors = {
				reportName: reportNameError,
				reportUrl: reportUrlError,
			};
			dashboardUrl.validConfig = !!(reportNameError || reportUrlError);
			isInValidForm = isInValidForm ? isInValidForm : dashboardUrl.validConfig;
		}
		return { dashboardUrls, isInValidForm };
	}, [dashboardUrls]);

	useEffect(() => {
		async function triggerInitialLoad() {
			await fetchDashboardUrlConfigList(dispatch);
		}
		triggerInitialLoad();
	}, [dispatch]);

	const getDashboardsList = useCallback(async () => {
		await fetchDashboardUrlConfigList(dispatch);
	}, []);

	const saveScheduledReportsList = useCallback(async () => {
		const { dashboardUrls } = state;
		await saveDashboardUrlConfigList(dispatch, Object.values(dashboardUrls));
	}, [state]);

	const clearDashboardConfigChanges = useCallback(() => {
		clearDashboardConfigChangesAction(dispatch);
	}, []);

	const removeDashboardUrl = useCallback((reportId, index) => {
		removeDashboardUrlConfigAction(dispatch, reportId, index);
	}, []);

	const addDashboardUrlConfig = useCallback(() => {
		addDashboardUrlConfigAction(dispatch);
	}, []);

	const modifyDashboardUrlConfig = useCallback((reportId, fieldName, fieldValue) => {
		modifyDashboardUrlConfigAction(dispatch, reportId, fieldName, fieldValue);
	}, []);

	const reorderDashboardUrls = useCallback((reOrderedDashboardUrls) => {
		reorderDashboardUrlsAction(dispatch, reOrderedDashboardUrls);
	}, []);

	// filtering the enabled URLs from saved configuration URLs
	const filteredDashboardUrls = useMemo(() => {
		return dashboardUrlsBackup.filter((url) => !url.isHidden);
	}, [dashboardUrlsBackup]);

	return (
		<Context.Provider
			value={{
				...state,
				...validateDashboardUrls,
				isModified,
				getDashboardsList,
				filteredDashboardUrls,
				saveScheduledReportsList,
				addDashboardUrlConfig,
				removeDashboardUrl,
				modifyDashboardUrlConfig,
				clearDashboardConfigChanges,
				reorderDashboardUrls,
			}}
		>
			{children}
		</Context.Provider>
	);
};
